import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useLayoutEffect,
  useRef,
} from "react";
import type { TourStepProps } from "antd";
import { useLocation } from "react-router-dom";
import { resolveTourSteps } from "./utils/resolveTourSteps";
import {
  isBuildingDetailsPath,
  isPropertyDetailsPath,
  isUnitDetailsPath,
  nextSetupPhaseFromNavigation,
  readSetupWalkthroughActive,
  readSetupWalkthroughPhase,
  type SetupWalkthroughPhase,
  writeSetupWalkthroughActive,
  writeSetupWalkthroughPhase,
} from "./utils/setupWalkthrough";
import { ROUTES } from "~/utils/routing/routeUtils";

/**
 * Ant Design's TourStepProps.target disallows `() => HTMLElement | null`
 * (only `() => HTMLElement` or `() => null` separately). Our configs use
 * queryTourTarget which returns null when the node is missing; resolveTourSteps
 * filters those out before the tour opens.
 */
export interface TourStep extends Omit<TourStepProps, "target"> {
  target?: HTMLElement | (() => HTMLElement | null) | null;
  // Optional: recommended next tours (for navigating to other pages)
  recommendedTours?: {
    label: string;
    path: string;
  }[];
  // Optional: tab navigation info (for tab-aware tours)
  tabNavigation?: {
    currentTab: string;
    tabOrder: string[]; // Page-specific tab order
  };
}

// Pending tour info for auto-starting after navigation
export interface PendingTour {
  path: string; // The path to navigate to (or current path for tab changes)
  tab?: string; // Optional: specific tab to show tour for
}

interface TourContextType {
  open: boolean;
  current: number;
  steps: TourStep[];
  pendingTour: PendingTour | null;
  setupWalkthroughActive: boolean;
  setupWalkthroughPhase: SetupWalkthroughPhase;
  startTour: (steps: TourStep[]) => void;
  closeTour: () => void;
  /** Hide the popover/mask without clearing steps — setup walkthrough stays active. */
  hideTourTip: () => void;
  /** Re-open a previously hidden tip (same steps/current). */
  showTourTip: () => void;
  nextStep: () => void;
  prevStep: () => void;
  setCurrent: (step: number) => void;
  setPendingTour: (pending: PendingTour | null) => void;
  clearPendingTour: () => void;
  startSetupWalkthrough: () => void;
  endSetupWalkthrough: () => void;
  setSetupWalkthroughPhase: (phase: SetupWalkthroughPhase) => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return context;
};

interface TourProviderProps {
  children: React.ReactNode;
}

export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(0);
  const [steps, setSteps] = useState<TourStep[]>([]);
  const [pendingTour, setPendingTourState] = useState<PendingTour | null>(null);
  const [setupWalkthroughActive, setSetupWalkthroughActive] = useState(
    readSetupWalkthroughActive
  );
  const [setupWalkthroughPhase, setSetupWalkthroughPhaseState] =
    useState<SetupWalkthroughPhase>(readSetupWalkthroughPhase);
  const prevPathnameRef = useRef(location.pathname);
  const stepsRef = useRef<TourStep[]>([]);
  stepsRef.current = steps;

  const startTour = useCallback((newSteps: TourStep[]) => {
    const resolved = resolveTourSteps(newSteps);
    if (resolved.length === 0) {
      setOpen(false);
      setCurrent(0);
      setSteps([]);
      return;
    }
    setSteps(resolved);
    setCurrent(0);
    setOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setOpen(false);
    setCurrent(0);
    setSteps([]);
  }, []);

  const hideTourTip = useCallback(() => {
    setOpen(false);
  }, []);

  const showTourTip = useCallback(() => {
    if (stepsRef.current.length > 0) {
      setOpen(true);
    }
  }, []);

  const setPendingTour = useCallback((pending: PendingTour | null) => {
    setPendingTourState(pending);
  }, []);

  const clearPendingTour = useCallback(() => {
    setPendingTourState(null);
  }, []);

  // Advance setup phase before child effects (GuideButton) auto-resume tours.
  // Also queue a pendingTour so post-create redirects reliably restart guidance.
  useLayoutEffect(() => {
    const previous = prevPathnameRef.current;
    const next = location.pathname;
    prevPathnameRef.current = next;

    if (!setupWalkthroughActive || previous === next) {
      return;
    }

    const advanced = nextSetupPhaseFromNavigation(
      previous,
      next,
      setupWalkthroughPhase
    );
    if (advanced !== setupWalkthroughPhase) {
      setSetupWalkthroughPhaseState(advanced);
      writeSetupWalkthroughPhase(advanced);
    }

    // Explicit handoff after create redirects (more reliable than path-only resume)
    if (previous === ROUTES.addProperty && isPropertyDetailsPath(next)) {
      setPendingTourState({ path: next, tab: "buildingList" });
      return;
    }
    if (previous === ROUTES.addBuilding && isBuildingDetailsPath(next)) {
      setPendingTourState({ path: next, tab: "unitList" });
      return;
    }
    if (
      previous === ROUTES.addUnit &&
      (isBuildingDetailsPath(next) || isUnitDetailsPath(next))
    ) {
      setPendingTourState({ path: next });
    }
  }, [location.pathname, setupWalkthroughActive, setupWalkthroughPhase]);

  const startSetupWalkthrough = useCallback(() => {
    setSetupWalkthroughActive(true);
    setSetupWalkthroughPhaseState("property");
    writeSetupWalkthroughActive(true);
    writeSetupWalkthroughPhase("property");
  }, []);

  const endSetupWalkthrough = useCallback(() => {
    setSetupWalkthroughActive(false);
    setSetupWalkthroughPhaseState("property");
    writeSetupWalkthroughActive(false);
  }, []);

  const setSetupWalkthroughPhase = useCallback(
    (phase: SetupWalkthroughPhase) => {
      setSetupWalkthroughPhaseState(phase);
      writeSetupWalkthroughPhase(phase);
    },
    []
  );

  const nextStep = useCallback(() => {
    setCurrent((prev) => {
      if (prev < steps.length - 1) {
        return prev + 1;
      }
      return prev;
    });
  }, [steps.length]);

  const prevStep = useCallback(() => {
    setCurrent((prev) => {
      if (prev > 0) {
        return prev - 1;
      }
      return prev;
    });
  }, []);

  const handleSetCurrent = useCallback(
    (step: number) => {
      if (step >= 0 && step < steps.length) {
        setCurrent(step);
      }
    },
    [steps.length]
  );

  const value: TourContextType = {
    open,
    current,
    steps,
    pendingTour,
    setupWalkthroughActive,
    setupWalkthroughPhase,
    startTour,
    closeTour,
    hideTourTip,
    showTourTip,
    nextStep,
    prevStep,
    setCurrent: handleSetCurrent,
    setPendingTour,
    clearPendingTour,
    startSetupWalkthrough,
    endSetupWalkthrough,
    setSetupWalkthroughPhase,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};
